import { computed, ref } from 'vue';
import { isAxiosError } from 'axios';
import { useModal } from 'vue-final-modal';
import { useCustomToast } from '@/shared/lib';
import { ConfirmationModal } from '@/shared/ui';
import { trainingMatrixApi } from '@/entities/Training';
import { buildTrainingMatrixEntries } from '../lib/buildTrainingMatrixEntries';
import SaveIcon from '@/shared/assets/icons/SaveIcon.svg';
import InfoBoldIcon from '@/shared/assets/icons/InfoBoldIcon.svg';

const MIN_REVISION = 1;
const MAX_REVISION = 999;

function formatRevisionNumber(revision: number): string {
  return String(revision).padStart(2, '0');
}

function getServerErrorMessage(error: unknown, fallback: string): string {
  return isAxiosError(error) && typeof error.response?.data?.message === 'string'
    ? error.response.data.message
    : fallback;
}

export function useEditTrainingMatrixRevision(onSaved: () => Promise<void> | void) {
  const toast = useCustomToast();

  const isEditing = ref(false);
  const editableValue = ref(MIN_REVISION);
  const isSaving = ref(false);
  const originalRevision = ref(0);
  let pendingRevision = 0;

  const modalTitle = ref('');
  const modalText = ref('');
  const modalIcon = ref(SaveIcon);

  const canDecrement = computed(() => editableValue.value > MIN_REVISION);
  const canIncrement = computed(() => editableValue.value < MAX_REVISION);
  const canSave = computed(() => editableValue.value !== originalRevision.value);

  const { open: openModal, close: closeModal } = useModal({
    component: ConfirmationModal,
    attrs: {
      clickToClose: false,
      escToClose: false,
      get title() {
        return modalTitle.value;
      },
      get text() {
        return modalText.value;
      },
      get icon() {
        return modalIcon.value;
      },
      iconAlt: 'Confirmar',
      cancelButtonText: 'Cancelar',
      actionButtonText: 'Confirmar',
      onClose: () => {
        isSaving.value = false;
        closeModal();
      },
      onConfirm: () => performSave(pendingRevision),
    },
  });

  function startEdit(currentRevision: number): void {
    originalRevision.value = currentRevision;
    editableValue.value = currentRevision > 0 ? currentRevision : MIN_REVISION;
    isEditing.value = true;
  }

  function cancelEdit(): void {
    editableValue.value = originalRevision.value > 0 ? originalRevision.value : MIN_REVISION;
    isEditing.value = false;
  }

  function increment(): void {
    if (!canIncrement.value) return;
    editableValue.value += 1;
  }

  function decrement(): void {
    if (!canDecrement.value) return;
    editableValue.value -= 1;
  }

  function setValue(rawValue: string): void {
    const parsed = Math.trunc(Number(rawValue));
    if (!Number.isFinite(parsed)) return;
    editableValue.value = Math.min(MAX_REVISION, Math.max(MIN_REVISION, parsed));
  }

  async function performSave(revision: number): Promise<void> {
    closeModal();
    isSaving.value = true;
    try {
      const current = await trainingMatrixApi.getCurrent({ allItems: true });
      const entries = buildTrainingMatrixEntries(current.data.positions);
      await trainingMatrixApi.saveRevision(revision, entries);

      toast.showSuccessToast({ title: 'Revisão salva com sucesso', type: 'success' });
      isEditing.value = false;
      await onSaved();
    } catch (error) {
      toast.showErrorToast({
        title: getServerErrorMessage(error, 'Erro ao salvar revisão. Tente novamente'),
        type: 'error',
      });
    } finally {
      isSaving.value = false;
    }
  }

  async function requestSave(): Promise<void> {
    if (!canSave.value || isSaving.value) return;

    isSaving.value = true;
    const revision = editableValue.value;
    pendingRevision = revision;

    try {
      const result = await trainingMatrixApi.checkRevisionExists(revision);
      if (result.data.exists) {
        modalIcon.value = InfoBoldIcon;
        modalTitle.value = `Tem certeza que deseja sobrescrever a Revisão ${formatRevisionNumber(revision)}?`;
        modalText.value =
          'Os dados da matriz vinculada a esta revisão serão substituídos. A ação não pode ser desfeita.';
      } else {
        modalIcon.value = SaveIcon;
        modalTitle.value = `Tem certeza que deseja salvar a Revisão ${formatRevisionNumber(revision)}?`;
        modalText.value = 'Uma nova versão da matriz será criada com os dados atuais.';
      }

      openModal();
    } catch (error) {
      isSaving.value = false;
      toast.showErrorToast({
        title: getServerErrorMessage(error, 'Erro ao verificar revisão. Tente novamente'),
        type: 'error',
      });
    }
  }

  return {
    isEditing,
    editableValue,
    isSaving,
    canSave,
    canDecrement,
    canIncrement,
    maxRevision: MAX_REVISION,
    startEdit,
    cancelEdit,
    increment,
    decrement,
    setValue,
    requestSave,
  };
}
